import type { GamePlayerStatColumnEnum, GPPSPlayerResponse, GPGamePlayerResponse } from "../../types";
import { PlayerStatCalculationResult, PlayerStatType } from "./definition";

export type GamePlayerStatLookup = Map<number, Map<GamePlayerStatColumnEnum, string | null>>;

const PLAYER_STAT_TYPE_TO_COLUMNS: Partial<Record<PlayerStatType, GamePlayerStatColumnEnum[]>> = {
	[PlayerStatType.ThrowsRatio]: ["throwsSuccess", "throwsTotal", "throwsRatio"],
	[PlayerStatType.AveragePassLength]: ["averagePassLength"],
	[PlayerStatType.LaunchPercent]: ["launchPercent"],
	[PlayerStatType.DefensiveActionsOutsidePenaltyArea]: ["defensiveActionsOpaTotal"],
	[PlayerStatType.AverageDefensiveActionDistance]: ["averageDefensiveActionDistance"],
	[PlayerStatType.CrossesFaced]: ["crossesFacedTotal"],
	[PlayerStatType.CrossesStoppedRatio]: ["crossesStoppedTotal", "crossesFacedTotal", "crossesStoppedRatio"],
	[PlayerStatType.SavePercent]: ["savePercent"],
};

export function isFullMatchPeriodSelection(selectedPeriods: string[], allPeriodKeys: string[]): boolean {
	if (allPeriodKeys.length === 0 || selectedPeriods.length !== allPeriodKeys.length) {
		return false;
	}

	const selectedPeriodSet = new Set(selectedPeriods);

	return allPeriodKeys.every((periodKey) => selectedPeriodSet.has(periodKey));
}

export function getPrecomputedStatsLookup(players: GPPSPlayerResponse[]): GamePlayerStatLookup {
	const lookup: GamePlayerStatLookup = new Map();

	players.forEach((player) => {
		const columnMap = new Map<GamePlayerStatColumnEnum, string | null>();
		player.columns.forEach((cell) => {
			columnMap.set(cell.type, cell.value);
		});
		lookup.set(player.id, columnMap);
	});

	return lookup;
}

function getColumnValue(
	lookup: GamePlayerStatLookup,
	gamePlayerId: number,
	column: GamePlayerStatColumnEnum,
): string | null {
	return lookup.get(gamePlayerId)?.get(column) ?? null;
}

function parseNumericValue(value: string | null): number | null {
	if (value === null || value === "") {
		return null;
	}

	const parsed = Number(value);
	if (Number.isNaN(parsed)) {
		return null;
	}

	return parsed;
}

function createBaseResult(
	player: GPGamePlayerResponse,
	statType: PlayerStatType,
): PlayerStatCalculationResult {
	return {
		url: player.playerUrl,
		playerId: player.id,
		statType,
		label: "",
		value: null,
		valueFormatted: null,
		valueSort: 0,
		events: [],
		filterValues: [],
		isFilterable: false,
		isUseful: false,
	};
}

function formatRatioResult(
	result: PlayerStatCalculationResult,
	successValue: string | null,
	totalValue: string | null,
	percentValue: string | null,
): PlayerStatCalculationResult {
	const success = parseNumericValue(successValue);
	const total = parseNumericValue(totalValue);
	const percent = parseNumericValue(percentValue);

	if (success === null || total === null || total === 0) {
		return result;
	}

	const computedPercent = percent ?? Math.round((success / total) * 100);

	result.value = computedPercent;
	result.valueFormatted = `${success}/${total}`;
	result.valueSort = (computedPercent * 1000) + total;
	result.isUseful = true;

	return result;
}

function formatPercentResult(
	result: PlayerStatCalculationResult,
	percentValue: string | null,
): PlayerStatCalculationResult {
	const percent = parseNumericValue(percentValue);
	if (percent === null) {
		return result;
	}

	result.value = percent;
	result.valueFormatted = `${percent}%`;
	result.valueSort = percent;
	result.isUseful = true;

	return result;
}

function formatCountResult(
	result: PlayerStatCalculationResult,
	countValue: string | null,
): PlayerStatCalculationResult {
	const count = parseNumericValue(countValue);
	if (count === null) {
		return result;
	}

	result.value = count;
	result.valueFormatted = String(count);
	result.valueSort = count;
	result.isUseful = count !== 0;

	return result;
}

function formatAverageResult(
	result: PlayerStatCalculationResult,
	averageValue: string | null,
): PlayerStatCalculationResult {
	const average = parseNumericValue(averageValue);
	if (average === null) {
		return result;
	}

	result.value = average;
	result.valueFormatted = average.toFixed(1);
	result.valueSort = average;
	result.isUseful = true;

	return result;
}

export function formatPrecomputedGoalkeeperStat(
	statType: PlayerStatType,
	player: GPGamePlayerResponse,
	lookup: GamePlayerStatLookup,
): PlayerStatCalculationResult {
	const result = createBaseResult(player, statType);

	if (player.id === null) {
		return result;
	}

	const columns = PLAYER_STAT_TYPE_TO_COLUMNS[statType];
	if (columns === undefined) {
		return result;
	}

	switch (statType) {
		case PlayerStatType.ThrowsRatio:
			return formatRatioResult(
				result,
				getColumnValue(lookup, player.id, "throwsSuccess"),
				getColumnValue(lookup, player.id, "throwsTotal"),
				getColumnValue(lookup, player.id, "throwsRatio"),
			);
		case PlayerStatType.CrossesStoppedRatio:
			return formatRatioResult(
				result,
				getColumnValue(lookup, player.id, "crossesStoppedTotal"),
				getColumnValue(lookup, player.id, "crossesFacedTotal"),
				getColumnValue(lookup, player.id, "crossesStoppedRatio"),
			);
		case PlayerStatType.LaunchPercent:
		case PlayerStatType.SavePercent:
			return formatPercentResult(result, getColumnValue(lookup, player.id, columns[0]));
		case PlayerStatType.AveragePassLength:
		case PlayerStatType.AverageDefensiveActionDistance:
			return formatAverageResult(result, getColumnValue(lookup, player.id, columns[0]));
		case PlayerStatType.DefensiveActionsOutsidePenaltyArea:
			return formatCountResult(result, getColumnValue(lookup, player.id, "defensiveActionsOpaTotal"));
		case PlayerStatType.CrossesFaced:
			return formatCountResult(result, getColumnValue(lookup, player.id, "crossesFacedTotal"));
		default:
			return result;
	}
}
